import { fetchAuthSession } from 'aws-amplify/auth';

/** Smart intake (PRD §6.2): link or image in, a draft to check out. */

export type IntakeField =
  | 'type'
  | 'titleCN'
  | 'titleEN'
  | 'providerName'
  | 'descriptionCN'
  | 'descriptionEN'
  | 'addressLine'
  | 'city'
  | 'region'
  | 'ageMin'
  | 'ageMax'
  | 'participation'
  | 'language'
  | 'category'
  | 'priceNoteCN'
  | 'priceNoteEN'
  | 'scheduleNoteCN'
  | 'scheduleNoteEN'
  | 'occurrences'
  | 'externalUrl';

export interface IntakeDraft {
  type: 'PROVIDER' | 'EVENT' | null;
  titleCN: string | null;
  titleEN: string | null;
  providerName: string | null;
  descriptionCN: string | null;
  descriptionEN: string | null;
  addressLine: string | null;
  city: string | null;
  region: string | null;
  ageMin: number | null;
  ageMax: number | null;
  participation: string | null;
  language: string | null;
  category: string | null;
  priceNoteCN: string | null;
  priceNoteEN: string | null;
  scheduleNoteCN: string | null;
  scheduleNoteEN: string | null;
  occurrences: { date: string; start: string; end: string | null }[];
  externalUrl: string | null;
}

export interface IntakeJob {
  id: string;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'ABANDONED';
  error: string | null;
  draftPickId: string | null;
  draft: IntakeDraft | null;
  confidences: Record<IntakeField, number> | null;
  /** The page would not open, so the draft comes (or is coming) from web search. */
  viaSearch: boolean;
  /** Pages a web-search draft was read from. */
  sources: string[];
}

/** Keeps the server's error code, which the generic apiRequest drops. */
export class IntakeRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  const res = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    ...init,
    headers: {
      ...(init.body instanceof Blob ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body?.success) {
    throw new IntakeRequestError(
      res.status,
      (body?.code as string | undefined) ?? (body?.error?.code as string | undefined) ?? null,
      typeof body?.error === 'string' ? body.error : `HTTP ${res.status}`
    );
  }
  return body.data as T;
}

/** Admin calls start team drafts: no per-person quota, listed with the team's Picks. */
export function intakeApi(scope: 'parent' | 'team') {
  const base = scope === 'team' ? '/admin/picks' : '/picks';
  return {
    async enabled(): Promise<boolean> {
      // A parent's account goes along: once their recognitions for today are used up
      // the answer is no, and the form opens on manual entry. The team has no such cap.
      if (scope === 'parent') {
        const data = await call<{ enabled: boolean }>('/picks/intake/enabled');
        return data.enabled === true;
      }
      const res = await fetch(`${import.meta.env.VITE_API_URL}/picks/intake/enabled`);
      const body = await res.json().catch(() => null);
      return body?.data?.enabled === true;
    },
    upload(file: Blob): Promise<{ uploadId: string }> {
      return call(`${base}/uploads`, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });
    },
    start(input: { kind: 'URL'; url: string } | { kind: 'IMAGE'; uploadId: string }) {
      return call<{ jobId: string }>(`${base}/intake`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    get(jobId: string) {
      return call<IntakeJob>(`/picks/intake/${encodeURIComponent(jobId)}`);
    },
    abandon(jobId: string) {
      return call<{ status: IntakeJob['status']; draftPickId: string | null }>(
        `/picks/intake/${encodeURIComponent(jobId)}/abandon`,
        { method: 'POST' }
      );
    },
  };
}

export const LOW_CONFIDENCE = 0.6;
export const INTAKE_WAIT_MS = 60_000;
/** A web search fallback takes longer: the wait restarts at this length when it begins. */
export const INTAKE_SEARCH_WAIT_MS = 120_000;
export const POLL_MS = 2000;
